import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import {
  NO_ZERO_RULE,
  ONLY_NUMBERS_RULE,
} from '@/common/components/base/input/textInput/textInput.rules';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import PercentageInput from '@/common/components/base/percentageInput/PercentageInput';
import FlowButton from '@/common/components/flowButton/FlowButton';
import useDebounce from '@/common/hooks/useDebounce';
import { getVehicleColorOption } from '@/common/queries/selections/vehicleColor/vehicleColorOption';
import { getVehicleProductYearOption } from '@/common/queries/selections/vehicleProductYear/vehicleProductYearOption';
import { InsuranceABV } from '@/common/types/SelectionOptions.type';

import { requiredText } from '@/common/utils/rules/publicRules';
import ImagesFabMainModal from '@/modules/adjusterReportModules/adjusterReport/components/imagesFab/ImagesFabMainModal';
import formatNumberWithCommas from '@/modules/adjusterReportModules/IncidentParticipants/utils/formatNumberWithCommas';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { getBodyVehicleInformationOption } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyVehicleInformation/getBodyVehicleInformationOption';
import { patchBodyPartTwoVehicleInformationOption } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyPartTwoVehicleInformation/patchBodyPartTwoVehicleInformationOption';
import { BodyVehicleInformationPartTwoFormType } from '@/modules/adjusterReportModules/Insurer-information/types/BodyVehicleInformationType';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Flex } from 'styled-system/jsx';

const BodyPartTwo = () => {
  const methods = useForm<BodyVehicleInformationPartTwoFormType>({
    mode: 'onChange',
  });
  const { id } = useParams();
  const debounce = useDebounce();
  const { data: fileDetail } = useQuery(fileDetailQueryOption({ id: +id! }));
  const navigate = useNavigate();
  const [colorName, setColorName] = useState('');
  const { data: vehicleColors } = useQuery({
    ...getVehicleColorOption({
      insurance: fileDetail?.insurance as InsuranceABV,
      name: colorName,
    }),
    enabled: !!fileDetail?.insurance,
  });
  const { data: vehicleProductsYears } = useQuery(
    getVehicleProductYearOption(),
  );
  const { mutate: damagedPartTwoMutation, isPending: patchPending } =
    useMutation(patchBodyPartTwoVehicleInformationOption());

  const submitHandler = (data: BodyVehicleInformationPartTwoFormType) => {
    damagedPartTwoMutation(
      {
        id: +id!,
        data: {
          vehicle_manufacturing_year: data?.vehicle_manufacturing_year?.label!,
          vehicle_chassis: data?.vehicle_chassis!,
          vehicle_engine: data?.vehicle_engine!,
          vehicle_color: data?.vehicle_color?.value!,
          vehicle_vin: data?.vehicle_vin!,
          fault_percentage: data?.fault_percentage!,
          vehicle_price: Number(
            (data?.vehicle_price ?? '0').toString().replace(/,/g, ''),
          ),
        },
      },
      {
        onSuccess: () => {
          navigate(
            ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
              id: id!,
            }).bodyDamageInformationTabsPage,
            {
              replace: true,
            },
          );
        },
      },
    );
  };
  const { data: vehicleInformationData } = useQuery(
    getBodyVehicleInformationOption({
      id: +id!,
    }),
  );

  const vehicleTargetPart =
    vehicleInformationData?.vehicle_type?.name === 'motorcycle'
      ? 'شماره تنه'
      : 'شماره شاسی';

  useEffect(() => {
    if (vehicleInformationData) {
      methods.reset({
        vehicle_manufacturing_year:
          vehicleInformationData?.vehicle_manufacturing_year
            ? {
                label: vehicleInformationData?.vehicle_manufacturing_year,
                value: vehicleInformationData?.vehicle_manufacturing_year,
              }
            : null,
        vehicle_color: vehicleInformationData?.vehicle_color
          ? {
              label: vehicleInformationData?.vehicle_color.name,
              value: vehicleInformationData?.vehicle_color.id,
            }
          : null,
        vehicle_vin: vehicleInformationData?.vehicle_vin,
        vehicle_chassis: vehicleInformationData?.vehicle_chassis,
        vehicle_engine: vehicleInformationData?.vehicle_engine,
        fault_percentage: vehicleInformationData?.fault_percentage,
        vehicle_price: formatNumberWithCommas(
          vehicleInformationData?.vehicle_price ?? '',
        ),
      });
    }
  }, [vehicleInformationData]);

  const handleColorOnChange = (value: string) => {
    if (value) {
      debounce(() => {
        setColorName(value);
      }, 1000);
    } else {
      setColorName(value);
    }
  };
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submitHandler)}>
          <Flex direction="column" gap={1}>
            <ModalInput
              name="vehicle_manufacturing_year"
              label="سال ساخت"
              placeholder="انتخاب کنید"
              options={vehicleProductsYears?.map((item) => ({
                label: item?.fa_const,
                value: item?.const,
              }))}
              rules={{ required: { message: requiredText, value: true } }}
            />

            <ModalInput
              name="vehicle_color"
              label="رنگ وسیله‌نقلیه"
              placeholder="انتخاب کنید"
              options={vehicleColors?.map((item) => {
                return {
                  label: item?.name,
                  value: item?.id,
                };
              })}
              searchOptions={{
                onChange: handleColorOnChange,
                placeholder: 'جستجو رنگ',
              }}
              rules={{ required: { message: requiredText, value: true } }}
            />
            <TextInput
              name="vehicle_vin"
              label="شماره VIN"
              placeholder="شماره VIN را وارد کنید"
              max={17}
              rules={{
                required: { message: requiredText, value: true },
                pattern: {
                  value: /^[A-Z0-9]{17}$/,
                  message:
                    'شماره VIN باید 17 کاراکتر و شامل حروف بزرگ انگلیسی و یا اعداد باشد',
                },
              }}
            />
            <TextInput
              name="vehicle_chassis"
              label={vehicleTargetPart}
              placeholder={`${vehicleTargetPart} را وارد کنید`}
              rules={{ required: { message: requiredText, value: true } }}
            />
            <TextInput
              name="vehicle_engine"
              label="شماره موتور"
              placeholder="شماره موتور را وارد کنید"
              rules={{ required: { message: requiredText, value: true } }}
            />
            <PercentageInput
              percentage={10}
              label="درصد تقصیر"
              maxPercentage={100}
              name="fault_percentage"
              rules={{ required: requiredText, ...ONLY_NUMBERS_RULE }}
              placeHolder="درصد تقصیر را وارد کنید"
            />
            <NumberInputWithWords
              name="vehicle_price"
              label="   ارزش روز وسیله‌نقلیه (ریال)"
              hasZeroAsDefault={false}
              rules={{
                required: { message: requiredText, value: true },
                ...NO_ZERO_RULE,
              }}
            />
            <FlowButton
              text="ثبت"
              disabled={patchPending || !methods?.formState?.isValid}
              previousStepHandler={() => {
                navigate(
                  ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
                    id: id!,
                  }).VEHICLE_INFORMATION.partOne,
                  {
                    replace: true,
                  },
                );
              }}
            />
          </Flex>{' '}
        </form>
      </FormProvider>
      <ImagesFabMainModal state="body" />
    </>
  );
};

export default BodyPartTwo;
